import { CapacityPlan, CapacityPlanParams, PlanDiscipline, PLAN_DISCIPLINES } from '../types';
import { STORAGE_KEYS } from '../constants';

const DEFAULT_PARAMS: CapacityPlanParams = {
  hoursPerShift: 12,
  daysPerWeek: 6,
  activeShifts: 1,
  shiftMultiplier: 1,
};

const MIN_WEEKS = 1;
const MAX_WEEKS = 52;

// Default week count for a fresh plan. Weeks are MANUAL — this planner never
// reads or mutates the project's own schedule, so plans stay a pure sandbox
// until validated with the client.
const DEFAULT_WEEKS = 8;

export function clampWeeks(n: number): number {
  return Math.min(Math.max(Math.round(n) || MIN_WEEKS, MIN_WEEKS), MAX_WEEKS);
}

/** Simple W1..Wn labels (no coupling to project dates). */
export function weekLabels(weekCount: number): string[] {
  return Array.from({ length: weekCount }, (_, i) => `W${i + 1}`);
}

/** Empty staffing map (0 headcount for every discipline across every week). */
function emptyStaffing(weekCount: number): Record<PlanDiscipline, number[]> {
  const staffing = {} as Record<PlanDiscipline, number[]>;
  PLAN_DISCIPLINES.forEach(d => { staffing[d.key] = Array(weekCount).fill(0); });
  return staffing;
}

/** A brand-new plan with the default week count. */
export function createDefaultPlan(projectId: string): CapacityPlan {
  return {
    projectId,
    params: { ...DEFAULT_PARAMS },
    weekCount: DEFAULT_WEEKS,
    staffing: emptyStaffing(DEFAULT_WEEKS),
    updatedAt: new Date().toISOString(),
  };
}

/** Resize a plan's staffing arrays to a new week count, preserving existing values. */
export function resizePlan(plan: CapacityPlan, weekCount: number): CapacityPlan {
  const staffing = {} as Record<PlanDiscipline, number[]>;
  PLAN_DISCIPLINES.forEach(d => {
    const prev = plan.staffing[d.key] || [];
    staffing[d.key] = Array.from({ length: weekCount }, (_, i) => prev[i] ?? 0);
  });
  return { ...plan, weekCount, staffing };
}

// ---- Derived metrics (same formula as the spreadsheet) ----

/** man-hours for a single (discipline, week) headcount. */
export function cellManHours(headcount: number, p: CapacityPlanParams): number {
  return headcount * p.hoursPerShift * p.daysPerWeek * p.activeShifts * p.shiftMultiplier;
}

export interface PlanMetrics {
  weeklyHeadcount: number[];     // total people per week
  weeklyManHours: number[];      // total man-hours per week
  cumulativeManHours: number[];  // running total
  byDiscipline: { key: PlanDiscipline; label: string; total: number; perWeek: number[] }[];
  totalManHours: number;
  peakWeekHeadcount: number;
}

export function computePlanMetrics(plan: CapacityPlan): PlanMetrics {
  const { weekCount, params } = plan;
  const weeklyHeadcount = Array(weekCount).fill(0);
  const weeklyManHours = Array(weekCount).fill(0);

  const byDiscipline = PLAN_DISCIPLINES.map(d => {
    const perWeek = (plan.staffing[d.key] || Array(weekCount).fill(0)).map(hc => cellManHours(hc, params));
    (plan.staffing[d.key] || []).forEach((hc, w) => {
      weeklyHeadcount[w] += hc;
      weeklyManHours[w] += cellManHours(hc, params);
    });
    // Respect a per-project renamed label, falling back to the default.
    const label = plan.disciplineLabels?.[d.key]?.trim() || d.label;
    return { key: d.key, label, total: perWeek.reduce((a, b) => a + b, 0), perWeek };
  });

  let running = 0;
  const cumulativeManHours = weeklyManHours.map(v => (running += v));

  return {
    weeklyHeadcount,
    weeklyManHours,
    cumulativeManHours,
    byDiscipline,
    totalManHours: weeklyManHours.reduce((a, b) => a + b, 0),
    peakWeekHeadcount: Math.max(0, ...weeklyHeadcount),
  };
}

// ---- Persistence (localStorage per project; Supabase-ready later) ----

const key = (projectId: string) => `${STORAGE_KEYS.CAPACITY_PLAN}_${projectId}`;

export function loadPlan(projectId: string): CapacityPlan {
  try {
    const raw = localStorage.getItem(key(projectId));
    if (raw) return JSON.parse(raw) as CapacityPlan;
  } catch { /* ignore corrupt cache */ }
  return createDefaultPlan(projectId);
}

export function savePlan(plan: CapacityPlan): void {
  try {
    localStorage.setItem(key(plan.projectId), JSON.stringify({ ...plan, updatedAt: new Date().toISOString() }));
  } catch { /* storage full / unavailable — non-fatal */ }
}
